// every application route in one place; web, admin and api import these.
// A path with an id is a function: the apps call it with the id, the API calls it with no argument to
// get the route pattern (for example /api/v1/admin/admins/:id), so each path is written once.
const id = (value = ':id') => value;

export const API_V1 = {
  health: '/api/v1/health',
  authSession: '/api/v1/auth/session',
  authSignOut: '/api/v1/auth/sign-out',
  me: '/api/v1/me',
  meProfile: '/api/v1/me/profile',
  meLife: '/api/v1/me/life',
  meLifeEnd: '/api/v1/me/life/end',
  mePlay: '/api/v1/me/play',
  mePlayTravel: '/api/v1/me/play/travel',
  // on the way somewhere: arrive now
  mePlaySkip: '/api/v1/me/play/skip',
  mePlayCancel: '/api/v1/me/play/cancel',
  mePlayFly: '/api/v1/me/play/fly',
  // a train from King's Cross
  mePlayTrain: '/api/v1/me/play/train',
  meFlight: '/api/v1/me/flight',
  meFlightOffer: '/api/v1/me/flight/offer',
  meJet: '/api/v1/me/jet',
  meJetBuy: '/api/v1/me/jet/buy',
  meJetSell: '/api/v1/me/jet/sell',
  // Rail Co.: my train company (start it, run it), my trains, and players' trains in the ticket office
  meRailCo: '/api/v1/me/railco',
  meRailCoTrains: '/api/v1/me/railco/trains',
  meRailCoTrain: (trainId?: string) => `/api/v1/me/railco/trains/${id(trainId)}`,
  meRailCoAction: '/api/v1/me/railco/action',
  meRailRate: '/api/v1/me/railco/rate',
  railOffers: '/api/v1/me/railco/offers',
  meBank: '/api/v1/me/bank',
  meBankSavings: '/api/v1/me/bank/savings',
  meBankBonds: '/api/v1/me/bank/bonds',
  meBankBond: (bondId?: string) => `/api/v1/me/bank/bonds/${id(bondId)}`,
  meBankHistory: '/api/v1/me/bank/history',
  meHouses: '/api/v1/me/houses',
  meHousesMove: '/api/v1/me/houses/move',
  meHousesAction: '/api/v1/me/houses/action',
  meLets: '/api/v1/me/houses/lets',
  meCourses: '/api/v1/me/courses',
  meCoursesEnrol: '/api/v1/me/courses/enrol',
  meCourseQuiz: (courseId: string) => `/api/v1/me/courses/${courseId}/quiz`,
  meCourseAnswer: (courseId: string) => `/api/v1/me/courses/${courseId}/quiz/answer`,
  meStaff: '/api/v1/me/staff',
  meStaffMember: (id: string) => `/api/v1/me/staff/${id}`,
  meCars: '/api/v1/me/cars',
  meCar: (id: string) => `/api/v1/me/cars/${id}`,
  meCarService: (id: string) => `/api/v1/me/cars/${id}/service`,
  meFamily: '/api/v1/me/family',
  meFamilyAsks: '/api/v1/me/family/asks',
  meFamilyAsk: (id: string) => `/api/v1/me/family/asks/${id}`,
  meFamilyAnswer: (id: string) => `/api/v1/me/family/asks/${id}/answer`,
  meFamilyChildCare: (id: string) => `/api/v1/me/family/children/${id}/care`,
  meFamilyNanny: '/api/v1/me/family/nanny',
  meHustle: '/api/v1/me/hustle',
  meHustleBoard: '/api/v1/me/hustle/board',
  meHustlePros: '/api/v1/me/hustle/pros',
  meHustleJobs: '/api/v1/me/hustle/jobs',
  meHustleOffers: '/api/v1/me/hustle/offers',
  meHustleJob: (id: string) => `/api/v1/me/hustle/jobs/${id}`,
  meHustleLook: (id: string) => `/api/v1/me/hustle/jobs/${id}/look`,
  meHustleListing: '/api/v1/me/hustle/listing',
  meGist: '/api/v1/me/gist',
  meGistPost: (id: string) => `/api/v1/me/gist/${id}`,
  meGistFollow: (lifeId: string) => `/api/v1/me/gist/follow/${lifeId}`,
  meGistSearch: '/api/v1/me/gist/search',
  meGistTrends: '/api/v1/me/gist/trends',
  meGistNotices: '/api/v1/me/gist/notices',
  meGistNoticesSeen: '/api/v1/me/gist/notices/seen',
  meGistUnread: '/api/v1/me/gist/unread',
  meGistVoice: '/api/v1/me/gist/voice',
  meGistVoicePlay: (voiceId?: string) => `/api/v1/me/gist/voice/${id(voiceId)}`,
  meGistPhoto: '/api/v1/me/gist/photo',
  meGistPhotoFile: (photoId?: string) => `/api/v1/me/gist/photo/${id(photoId)}`,
  // Gov: the State House and the race; standing, voting (at City Hall), the Mayor's policy and announcement, reports
  meGov: '/api/v1/me/gov',
  meGovStand: '/api/v1/me/gov/stand',
  meGovVote: '/api/v1/me/gov/vote',
  meGovPolicy: '/api/v1/me/gov/policy',
  meGovAnnounce: '/api/v1/me/gov/announce',
  meGovReport: '/api/v1/me/gov/report',
  meHealth: '/api/v1/me/health',
  meHealthPharmacy: '/api/v1/me/health/pharmacy',
  meBets: '/api/v1/me/bets',
  meBetsControl: '/api/v1/me/bets/control',
  meLet: (letId?: string) => `/api/v1/me/houses/lets/${id(letId)}`,
  meFlightOffers: '/api/v1/me/flight/offers',
  meFlightOfferAccept: (offerId?: string) => `/api/v1/me/flight/offers/${id(offerId)}/accept`,
  meFlightOfferDecline: (offerId?: string) => `/api/v1/me/flight/offers/${id(offerId)}/decline`,
  mePlayActivities: '/api/v1/me/play/activities',
  // the profile screen
  meAccount: '/api/v1/me/account',
  meUsername: '/api/v1/me/username',
  meDisplayName: '/api/v1/me/display-name',
  meSettings: '/api/v1/me/settings',
  meLifeTraits: '/api/v1/me/life/traits',
  meLifeLook: '/api/v1/me/life/look',
  meGoals: '/api/v1/me/goals',
  meGoalsMission: '/api/v1/me/goals/missions',
  meGoalsDream: '/api/v1/me/goals/dream',
  meGoalsDreamClaim: '/api/v1/me/goals/dream/claim',
  meGoalsPerk: '/api/v1/me/goals/perks',
  // the player's house
  meHome: '/api/v1/me/home',
  meHomeLayout: '/api/v1/me/home/layout',
  meHomeBuy: '/api/v1/me/home/buy',
  meHomeSell: '/api/v1/me/home/sell',
  meHomeRent: '/api/v1/me/home/rent',
  // people: who you know and who is here
  mePeople: '/api/v1/me/people',
  mePersonActions: (lifeId?: string) => `/api/v1/me/people/${id(lifeId)}/actions`,
  // who is at a place right now (the place's card on the map shows them)
  mePlaceHere: (placeId?: string) => `/api/v1/me/places/${id(placeId)}/here`,
  // money between players, from a direct chat
  meMoneyAllowance: (lifeId?: string) => `/api/v1/me/money/allowance/${id(lifeId)}`,
  meMoneySend: '/api/v1/me/money/send',
  meMoneyAsk: '/api/v1/me/money/ask',
  meMoneyRequest: (requestId?: string) => `/api/v1/me/money/requests/${id(requestId)}`,
  meMoneyMeal: '/api/v1/me/money/meal',
  meNeighbours: '/api/v1/me/neighbours',
  meVisits: '/api/v1/me/visits',
  meVisitStart: '/api/v1/me/visits/start',
  meVisitLeave: '/api/v1/me/visits/leave',
  meVisitHome: '/api/v1/me/visits/home',
  meVisitInvite: '/api/v1/me/visits/invite',
  meVisitAnswer: (visitId?: string) => `/api/v1/me/visits/${id(visitId)}/answer`,
  meVisitSendHome: (visitId?: string) => `/api/v1/me/visits/${id(visitId)}/send-home`,
  // adverts on the map's boards (contracts adverts.ts): public to see, players book their own
  adverts: '/api/v1/public/adverts',
  advertImage: (advertId?: string) => `/api/v1/public/adverts/${id(advertId)}/image`,
  meAdverts: '/api/v1/me/adverts',
  meAdvertImage: (advertId?: string) => `/api/v1/me/adverts/${id(advertId)}/image`,
  meAdvertWithdraw: (advertId?: string) => `/api/v1/me/adverts/${id(advertId)}/withdraw`,
  // players' businesses (contracts business.ts)
  meBusinesses: '/api/v1/me/businesses',
  meBusiness: (businessId?: string) => `/api/v1/me/businesses/${id(businessId)}`,
  // players' shops, for those buying
  meShops: '/api/v1/me/shops',
  meShopBuy: (businessId?: string) => `/api/v1/me/shops/${id(businessId)}/buy`,
  // "🛍️ My shop": share one of your shops in a chat
  meChatShop: (conversationId?: string) => `/api/v1/me/chat/${id(conversationId)}/shop`,
  // chat
  ws: '/api/v1/ws',
  meChats: '/api/v1/me/chat',
  meChatOpen: '/api/v1/me/chat/open',
  meChatReadAll: '/api/v1/me/chat/read-all',
  meChatMessages: (conversationId?: string) => `/api/v1/me/chat/${id(conversationId)}/messages`,
  meChatRead: (conversationId?: string) => `/api/v1/me/chat/${id(conversationId)}/read`,
  meChatClear: (conversationId?: string) => `/api/v1/me/chat/${id(conversationId)}/clear`,
  meChatMessage: (messageId?: string) => `/api/v1/me/chat/messages/${id(messageId)}`,
  meChatMessageReport: (messageId?: string) => `/api/v1/me/chat/messages/${id(messageId)}/report`,
  meChatMessageReaction: (messageId?: string) => `/api/v1/me/chat/messages/${id(messageId)}/reaction`,
  meChatMessageForward: (messageId?: string) => `/api/v1/me/chat/messages/${id(messageId)}/forward`,
  meChatVoice: (conversationId?: string) => `/api/v1/me/chat/${id(conversationId)}/voice`,
  meChatVoiceAllow: (conversationId?: string) => `/api/v1/me/chat/${id(conversationId)}/voice-allow`,
  meChatVoicePlay: (messageId?: string) => `/api/v1/me/chat/voice/${id(messageId)}`,
  meChatGroups: '/api/v1/me/chat/groups',
  meChatGroup: (conversationId?: string) => `/api/v1/me/chat/groups/${id(conversationId)}`,
  adminChatReports: '/api/v1/admin/chat/reports',
  adminHustleReports: '/api/v1/admin/hustle/reports',
  adminHustleReport: (jobId?: string) => `/api/v1/admin/hustle/reports/${id(jobId)}`,
  adminGistReports: '/api/v1/admin/gist/reports',
  adminGistReport: (itemId?: string) => `/api/v1/admin/gist/reports/${id(itemId)}`,
  adminGistNews: '/api/v1/admin/gist/news',
  adminGistVoice: (voiceId?: string) => `/api/v1/admin/gist/voice/${id(voiceId)}`,
  adminGistPhotos: '/api/v1/admin/gist/photos',
  adminGistPhoto: (photoId?: string) => `/api/v1/admin/gist/photos/${id(photoId)}`,
  adminGistPhotoFile: (photoId?: string) => `/api/v1/admin/gist/photos/${id(photoId)}/file`,
  adminGov: '/api/v1/admin/gov',
  adminChatReport: (reportId?: string) => `/api/v1/admin/chat/reports/${id(reportId)}`,
  adminChatReportVoice: (reportId?: string) => `/api/v1/admin/chat/reports/${id(reportId)}/voice`,
  // alerts when the game is closed
  publicPushKey: '/api/v1/public/push-key',
  mePush: '/api/v1/me/push',
  // the bell
  meNotices: '/api/v1/me/notices',
  meNoticesRead: '/api/v1/me/notices/read',
  // the London Life team chat
  meTeam: '/api/v1/me/team',
  meTeamRead: '/api/v1/me/team/read',
  meTeamVote: (postId?: string) => `/api/v1/me/team/posts/${id(postId)}/vote`,
  adminTeamPosts: '/api/v1/admin/team/posts',
  adminTeamPost: (postId?: string) => `/api/v1/admin/team/posts/${id(postId)}`,
  characterOptions: '/api/v1/public/character-options',
  usernameAvailability: (username?: string) => `/api/v1/public/usernames/${id(username ?? ':username')}`,
  cityActivities: '/api/v1/public/world/activities',
  // the published content (places, insides, activities) and the admin's drafts and versions
  worldContent: '/api/v1/public/world/content',
  adminContent: '/api/v1/admin/content',
  adminContentDraft: '/api/v1/admin/content/draft',
  adminContentPublish: '/api/v1/admin/content/publish',
  adminContentRestore: (versionId?: string) => `/api/v1/admin/content/versions/${id(versionId)}/restore`,
  companyDetails: '/api/v1/public/company',
  adminCompanyDetails: '/api/v1/admin/settings/company',
  worldFacts: '/api/v1/public/world',
  // the Mayor, the policy in force and the announcement on everyone's map
  govBanner: '/api/v1/public/gov',
  consentSettings: '/api/v1/public/consent',
  presence: '/api/v1/public/presence',
  siteStats: '/api/v1/public/stats',
  adminSiteStats: '/api/v1/admin/settings/stats',
  adminConsentSettings: '/api/v1/admin/settings/consent',
  adminWorldFacts: '/api/v1/admin/settings/world',
  adminSessionSettings: '/api/v1/admin/settings/sessions',
  adminAdverts: '/api/v1/admin/adverts',
  adminAdvert: (advertId?: string) => `/api/v1/admin/adverts/${id(advertId)}`,
  adminAdvertImage: (advertId?: string) => `/api/v1/admin/adverts/${id(advertId)}/image`,
  adminAdvertSettings: '/api/v1/admin/adverts/settings',
  adminMe: '/api/v1/admin/me',
  adminTwoStepSetup: '/api/v1/admin/me/two-step/setup',
  adminTwoStepConfirm: '/api/v1/admin/me/two-step/confirm',
  adminTwoStepVerify: '/api/v1/admin/me/two-step/verify',
  adminTwoStepRecoveryCodes: '/api/v1/admin/me/two-step/recovery-codes',
  adminAdmins: '/api/v1/admin/admins',
  adminAdmin: (adminId?: string) => `/api/v1/admin/admins/${id(adminId)}`,
  adminAdminRoles: (adminId?: string) => `/api/v1/admin/admins/${id(adminId)}/roles`,
  adminAdminTwoStep: (adminId?: string) => `/api/v1/admin/admins/${id(adminId)}/two-step`,
  adminRoles: '/api/v1/admin/roles',
  adminRole: (roleId?: string) => `/api/v1/admin/roles/${id(roleId)}`,
  adminAudit: '/api/v1/admin/audit',
  // players (admin)
  adminPlayers: '/api/v1/admin/players',
  adminPlayer: (userId?: string) => `/api/v1/admin/players/${id(userId)}`,
  adminPlayerActions: (userId?: string) => `/api/v1/admin/players/${id(userId)}/actions`,
  // warnings from the moderators, shown to the player once
  meWarnings: '/api/v1/me/warnings',
  meWarningSeen: (warningId?: string) => `/api/v1/me/warnings/${id(warningId)}/seen`,
  // money (docs ECONOMY_LEDGER.md)
  adminEconomy: '/api/v1/admin/economy',
  adminEconomySettings: '/api/v1/admin/economy/settings',
  adminLedgerPlayers: '/api/v1/admin/ledger/players',
  adminLedgerAccountEntries: (accountId?: string) => `/api/v1/admin/ledger/accounts/${id(accountId)}/entries`,
  adminLedgerAccountFreeze: (accountId?: string) => `/api/v1/admin/ledger/accounts/${id(accountId)}/freeze`,
  adminLedgerTransaction: (transactionId?: string) => `/api/v1/admin/ledger/transactions/${id(transactionId)}`,
  adminLedgerReversal: (transactionId?: string) => `/api/v1/admin/ledger/transactions/${id(transactionId)}/reversal`,
  adminLedgerAdjustments: '/api/v1/admin/ledger/adjustments',
  adminLedgerApprovals: '/api/v1/admin/ledger/approvals',
  adminLedgerApprovalDecision: (approvalId?: string) => `/api/v1/admin/ledger/approvals/${id(approvalId)}/decision`,
  adminTransfers: '/api/v1/admin/ledger/transfers',
  adminTransferDecision: (transferId?: string) => `/api/v1/admin/ledger/transfers/${id(transferId)}/decision`,
} as const;
